import {
  Tree,
  gateHolds,
  landingPhase,
  phaseHolds,
  phaseOfNode,
  plansFromDto,
  processFromDto,
  projectPhases,
  schedule,
  type Hold,
  type PhaseKey,
  type ProcessDto,
  type ProcessStepDto,
  type ProjectProcess,
  type Schedule,
  type TradesDto,
} from '@nsb/core';
import { useEffect, useMemo } from 'react';
import { api, queryClient, useApi } from '../../api.ts';

/** Everything the editor reads from one ProcessDto, computed once per change. */
export interface ProcessModel {
  dto: ProcessDto;
  tree: Tree;
  process: ProjectProcess;
  schedule: Schedule;
  /** For each gate on the job that closes a phase, the steps of its phase not done it waits on by rule (core `phaseHolds`). */
  holds: Map<string, string[]>;
  /**
   * What holds each step of the job back, nearest first (core `gateHolds`; L-17 B): every gate and inspection not passed
   * holds everything after it, however far down. Read once, here, by everything that says so: a tile’s foot, its panel.
   * An archived job is kept as it ended (L-19 A): nothing holds anything back on it any more.
   */
  held: Map<string, Hold[]>;
  /**
   * The phase each step is done in, as the API reads it (core `projectPhases`): a task done in an earlier phase than its
   * step (design study 31, slice 4) is apart from its step's start in the order of work. What every core rule reading
   * that order is given here as there (`schedule`, `isReached`, `wouldLoop`, `link`).
   */
  phases: Map<string, PhaseKey>;
  /** Where a step of the tree would land if it were added now: its own phase while open, else late in the phase under way (core `landingPhase`). */
  landing: (key: string) => { phase: PhaseKey; late: boolean; from: PhaseKey | null };
  step: (id: string) => ProcessStepDto | undefined;
}

/** The model of a job's process as the API sent it: null until it is configured. */
export function processModel(dto: ProcessDto | undefined): ProcessModel | null {
  if (!dto || !dto.kind) return null;
  const tree = new Tree(dto.tree);
  // Each step in the phase it sits in, as the API read it (core `processFromDto`): a step come late is read where it landed.
  const process: ProjectProcess = processFromDto(dto);
  const phases = projectPhases(dto.tree, process, null);
  const sch = schedule(process, dto.today, plansFromDto(dto.steps), phases);
  const byId = new Map(dto.steps.map((s) => [s.id, s]));
  const landed = new Map<string, { phase: PhaseKey; late: boolean; from: PhaseKey | null }>();
  return {
    dto,
    tree,
    process,
    schedule: sch,
    phases,
    holds: phaseHolds(dto.tree, process),
    held: dto.kept === 'whole' ? new Map() : gateHolds(dto.tree, process),
    landing: (key) => {
      let got = landed.get(key);
      if (!got) {
        const l = landingPhase(tree, process, key);
        got = { ...l, from: l.late ? phaseOfNode(dto.tree, key) : null };
        landed.set(key, got);
      }
      return got;
    },
    step: (id) => byId.get(id),
  };
}

export function useProcessModel(dto: ProcessDto | undefined): ProcessModel | null {
  return useMemo(() => processModel(dto), [dto]);
}

const processKey = (projectId: string) => ['project', projectId, 'process'];
/** The master tree's lock-in: what saving it for projects would change (LockIn), read again after each draft save. */
export const lockInKey = ['builder', 'lock-in'];
/** The Process page's: the job with its draft on it (Still to decide, A). */
export const draftProcessKey = (projectId: string) => ['project', projectId, 'process', 'draft'];

/** The projects whose Process page is open: their changes go into the job's draft until saved. */
const drafting = new Set<string>();

/**
 * The job as its Process page shows it, with its draft on it (Still to decide, A; Jason, 2026-10-06: "there needs to
 * be a drafting/saving"): while the page is open, what it adds, leaves out or redraws is held in the draft
 * (`?draft=1`), nothing about the job changing until Save the changes. Setup and the other pages read the job itself.
 */
export function useDraftedProcess(projectId: string) {
  useEffect(() => {
    drafting.add(projectId);
    return () => {
      drafting.delete(projectId);
    };
  }, [projectId]);
  return useApi<ProcessDto>(draftProcessKey(projectId), `/projects/${projectId}/process?draft=1`);
}

/** The org's trades and performers, for a package's panel. */
export function useTrades() {
  return useApi<TradesDto>(['trades'], '/trades');
}

/** The job's process as it is, without its draft: the Process page, where the job is run. */
export function useJobProcess(projectId: string) {
  return useApi<ProcessDto>(processKey(projectId), `/projects/${projectId}/process`);
}

/** A change to the process: the API answers with the process as it now stands (on the Process page, with its draft on it). */
export async function processAction(projectId: string, path: string, body: unknown = {}, method?: string): Promise<ProcessDto> {
  const draft = drafting.has(projectId);
  const dto = await api<ProcessDto>(`/projects/${projectId}/process${path}${draft ? '?draft=1' : ''}`, { body: method === 'DELETE' ? undefined : body, method });
  const mine = draft ? draftProcessKey(projectId) : processKey(projectId);
  queryClient.setQueryData(mine, dto);
  // What reads the process: the home's next milestone and needs, the funnel, the client's view, time targets, and the
  // job itself where it is read without its draft.
  void queryClient.invalidateQueries({
    predicate: (q) => q.queryKey[0] === 'project' && q.queryKey[1] === projectId && JSON.stringify(q.queryKey) !== JSON.stringify(mine),
  });
  return dto;
}
